import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-bootstrap'
import { useState } from 'react'
import FormInput from '../../components/forms/formInput'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse'
import { createReminder, deleteReminder, updateReminder } from '../../services/reminder'
import { toLocalDateString } from '../../utils/helper'
import { getUsersAsOptions } from '../../services/companyUsers'
import AssigneeSelect from '../../components/selects/assigneeSelect'
import ParticipantsSelect from '../../components/selects/participantsSelect'
import SbrDrawer from '../../components/sbr/SbrDrawer'

const BellIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
);

const SetReminderModal = ({
    display,
    toggle,
    reminder
}) => {

    const { t } = useTranslation();
    const [participantsOptions, setParticipantsOptions] = useState([])

    const [formData, setFormData] = useState({})
    const queryClient = useQueryClient()

    const { data: dbParticipantsOptions, isLoading: isLoadingParticipantsOptions } = useQuery(['users-option'], () => getUsersAsOptions())

    useEffect(() => {

        if (!dbParticipantsOptions)
            return

        onQuerySuccess(dbParticipantsOptions, (result) => {
            if (result.length > 0) {
                setParticipantsOptions(result.map(d => ({
                    value: d.id, label: d.fullName, roles: d.roles
                })))
            }
        })

    }, [dbParticipantsOptions])

    const { mutate, isLoading } = useMutation(reminder.id ? updateReminder : createReminder, {
        onSuccess: onMutateSuccess((a) => {
            queryClient.invalidateQueries(['reminders', 'monthly'])
            onHide()
        })
    })

    const { mutate: remove, isLoading: deleting } = useMutation(deleteReminder, {
        onSuccess: onMutateSuccess((a) => {
            queryClient.invalidateQueries(['reminders', 'monthly'])
            onHide()
        })
    })

    const handleChange = (update) => {
        const data = { ...formData, ...update }
        setFormData(data)
    }

    const handleParticipantsChange = (participants) => {
        const data = { ...formData, participants: participants ?? [] }

        setFormData(data)
    }

    const onShow = () => {
        const data = reminder.id ? {
            ...reminder,
            participants: reminder.participants.map(p => p.id)
        } : reminder

        setFormData(data)
    }

    const onHide = () => {
        toggle(false)
        setFormData({})
    }

    const onSubmit = (e) => {
        e.preventDefault()
        const payload = { ...formData }
        payload.participants = formData.participants.map(id => ({ id }))

        mutate(payload)
    }

    return (
        <SbrDrawer
            show={display}
            onShow={onShow}
            onHide={onHide}
            secondary
            title={formData.id ? t('calendar.editReminder') : t('calendar.addReminder')}
            subtitle={formData.date ? toLocalDateString(formData.date, 'dd.MM.yyyy') : t('calendar.setReminderSubtitle')}
            icon={<BellIcon />}
            footer={
                <>
                    {formData.id && (
                        <button
                            type="button"
                            className="btn-sbr btn-ghost-sbr btn-sm-sbr"
                            onClick={() => remove(formData.id)}
                            disabled={deleting}
                            style={{ color: 'var(--bad)', borderColor: 'var(--bad-bd)', marginRight: 'auto' }}
                        >
                            {deleting ? `${t('common.delete')}…` : t('common.delete')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn-sbr btn-ghost-sbr btn-sm-sbr"
                        onClick={onHide}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        form="set-reminder"
                        className="btn-sbr btn-primary-sbr btn-sm-sbr"
                        disabled={isLoading}
                    >
                        {isLoading ? t('common.saving') : t('common.save')}
                    </button>
                </>
            }
        >
            <Form id="set-reminder" onSubmit={onSubmit}>
                <FormInput
                    label={t('calendar.title_field')}
                    value={formData?.title ?? ''}
                    onChange={e => handleChange({ title: e.target.value })}
                    className="mb-3"
                />

                <FormInput
                    label={t('common.notes')}
                    value={formData?.notes ?? ''}
                    onChange={e => handleChange({ notes: e.target.value })}
                    as="textarea"
                    className="mb-3"
                />

                <ParticipantsSelect
                    label={t('calendar.participants')}
                    isMulti
                    value={formData.participants}
                    onChange={opt => handleParticipantsChange(opt?.map(x => x.value))}
                />
            </Form>
        </SbrDrawer>
    )
}

export default SetReminderModal
